import { View, TextInput, SxProp, Pressable, useDripsyTheme } from 'dripsy';
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useStore } from '../../../../store/store';

import { TextAmount, TextCaption } from '../typography';
import { useAmountInput } from './useAmountInput.hook';
import { UniInputVariant, useInputTheme } from './useInputTheme.hook';

interface UniInputAmountProps {
  variant?: UniInputVariant;
  size?: 'small' | 'medium' | 'large';
  asset?: string;
  balance?: string | number;
  subText?: string;
  labelText?: string;
  labelRightText?: string;
  errorText?: string;
  error?: boolean;
  errorTextPadding?: number;
  sx?: SxProp;
  value?: string | number;
  max?: number;
  validator?: (value?: number) => void;
  onValueChange?: (value?: number, isMax?: boolean) => void;
  formatValueOnBlur?: (value?: number) => number | undefined;
  assetPrefix?: boolean;
  sxWrap?: SxProp;
  sxInput?: SxProp;
  maxFlag?: boolean;
  editable?: boolean;
  placeholderInput?: string;
  placeholderTextColor?: string;
  decimals?: number;
}

export const UniInputAmount: React.FC<UniInputAmountProps> = ({
  sx,
  variant,
  size = 'medium',
  asset = ' - ',
  balance,
  labelText,
  labelRightText,
  errorText,
  error,
  errorTextPadding,
  value,
  max,
  validator,
  onValueChange,
  formatValueOnBlur,
  assetPrefix = true,
  sxWrap,
  sxInput,
  maxFlag = true,
  editable = true,
  placeholderInput,
  placeholderTextColor,
  decimals
}) => {
  let inputVariant = variant;
  if (!inputVariant) {
    const store = useStore();
    inputVariant = store.inputVariant;
  }

  const amountInput = useAmountInput({
    max: max !== undefined ? max : balance,
    value,
    onValueChange,
    decimals
  });

  const inputTheme = useInputTheme({
    size,
    variant: inputVariant,
    isError: amountInput.value !== null && !!errorText
    // amountInput.value !== null &&
    // (amountInput.insufficientBalance || (amountInput.valueBig !== null && max !== undefined && amountInput.valueBig.gt(max)) || error)
  });

  return (
    <View sx={sx}>
      {labelText || labelRightText ? (
        <View sx={{ flexDirection: 'row', justifyContent: 'space-between' }}>
          {labelText ? (
            <TextCaption semiBold label themePrimary={variant === UniInputVariant.v3 && inputTheme.hasFocus}>
              {labelText}
            </TextCaption>
          ) : null}
          {labelRightText ? (
            <TextCaption semiBold label themePrimary={variant === UniInputVariant.v3 && inputTheme.hasFocus}>
              {labelRightText}
            </TextCaption>
          ) : null}
        </View>
      ) : null}
      <View sx={{ ...inputTheme.wrapperStyle, alignItems: 'baseline', ...sxWrap }}>
        {!!asset && <Prefix asset={asset} onFocus={inputTheme.focus} />}
        {!!assetPrefix && <PrefixDivider />}
        <TextInput
          ref={inputTheme.ref}
          sx={{ ...inputTheme.inputStyle, ...sxInput }}
          onFocus={inputTheme.onFocus}
          selection={amountInput.selection}
          onBlur={(e) => {
            inputTheme.onBlur();
            amountInput.formatValueNoZero();
            const formattedValue = formatValueOnBlur
              ? formatValueOnBlur(amountInput.valueBig?.toNumber())
              : amountInput.valueBig?.toNumber();
            if (formatValueOnBlur) {
              amountInput.formatInput({ inputValue: formattedValue?.toString(), inputSelection: { start: 0, end: 0 } });
            }
            validator && validator(formattedValue);
          }}
          value={amountInput.displayValue}
          onChange={amountInput.onChange}
          onTextInput={amountInput.onChange}
          keyboardType="numeric"
          returnKeyType="done"
          editable={editable}
          placeholder={placeholderInput}
          placeholderTextColor={placeholderTextColor}
        />
        {maxFlag && <Max onSetMax={amountInput.onSetMax} />}
      </View>
      {!!balance && (
        <Balance asset={asset} balance={balance !== undefined ? balance : ''} insufficientBalance={amountInput.insufficientBalance} />
      )}
      <ErrorText errorText={errorText} errorTextPadding={errorTextPadding} />
    </View>
  );
};

const Prefix = (props: { asset: string; onFocus: () => void }) => {
  return (
    <Pressable onPress={props.onFocus} sx={styles.prefix}>
      <TextCaption secondary>{props.asset}</TextCaption>
    </Pressable>
  );
};

const PrefixDivider = () => {
  return (
    <View
      sx={{
        height: '100%',
        paddingY: '$3',
        alignSelf: 'center'
      }}>
      <View
        sx={{
          flex: 1,
          width: 1,
          backgroundColor: '$borderField'
        }}
      />
    </View>
  );
};

const Max = (props: { onSetMax: () => void }) => {
  const { t } = useTranslation('earn');
  return (
    <Pressable sx={{ height: '100%' }} onPress={props.onSetMax}>
      {({ pressed }) => (
        <View
          sx={{
            ...styles.max,
            backgroundColor: pressed ? '$accent' : 'transparent'
          }}>
          <TextCaption semiBold hyperlink>
            {t('earn-invest::glossary.max')}
          </TextCaption>
        </View>
      )}
    </Pressable>
  );
};

const Balance = ({ asset, balance, insufficientBalance }: { balance?: string | number; insufficientBalance?: boolean; asset?: string }) => {
  const { t } = useTranslation('earn');

  const displayBalance = balance !== undefined ? balance : '';

  return (
    <View sx={styles.balance}>
      {!insufficientBalance && <TextCaption>{t('earn-invest::glossary.balance')}</TextCaption>}
      {insufficientBalance && <TextCaption danger>Not enough balance</TextCaption>}
      <TextAmount size="textCaption" currency={asset}>
        {displayBalance}
      </TextAmount>
    </View>
  );
};

const ErrorText = ({ errorText, errorTextPadding }: { errorText?: string; errorTextPadding?: number }) => {
  if (!errorText && !errorTextPadding) return null;

  if (!errorText && errorTextPadding) {
    return <View sx={{ height: errorTextPadding }} />;
  }

  return (
    <TextCaption danger sx={styles.errorText}>
      {errorText}
    </TextCaption>
  );
};

enum StyleName {
  container = 'container',
  wrapper = 'wrapper',
  max = 'max',
  balance = 'balance',
  prefix = 'prefix',
  errorText = 'errorText'
}

const styles: Record<StyleName, SxProp> = {
  container: {},
  wrapper: {},
  max: {
    flex: 1,
    paddingX: '$3',
    justifyContent: 'center'
  },
  balance: {
    mt: 4,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center'
  },
  prefix: {
    justifyContent: 'center',
    alignItems: 'center',
    paddingX: '$3'
  },
  errorText: {
    mt: '$1'
  }
};
